import { Inject, Component, OnDestroy, OnInit } from '@angular/core';
import { environment } from './../environments/environment';
import { Router, NavigationEnd, ActivatedRoute, NavigationStart } from '@angular/router';
import { DomSanitizer, Meta } from '@angular/platform-browser';
import { filter } from 'rxjs/operators';
import { LiveChatService } from './services/live-chat/live-chat.service';
import { CanonicalService } from './services/common/canonical.service';
import { Location } from '@angular/common';
import { UserService } from './services/user/user.service';
import { AuthManagerService } from './services/auth-manager/auth-manager.service';
import { HttpErrorResponse } from '@angular/common/http';
import { SocialTagsService } from './services/common/social-tags.service';
import { Subscription } from 'rxjs';
import { NgcCookieConsentService, NgcStatusChangeEvent, NgcInitializingEvent } from 'ngx-cookieconsent';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { ACCESS_TOKEN_KEY } from './constants/app.constants';
import { MatIconRegistry } from '@angular/material/icon';
import { GoogleMapsService } from './services/google-maps/google.maps.service';
import { PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  appVersion: string = environment.appVersion;
  statusChangeSubscription: Subscription;
  popupInitialized$: Subscription;
  apiLoaded = false;
  isBrowser: boolean;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private metaService: Meta,
    private canonicalService: CanonicalService,
    private location: Location,
    private userService: UserService,
    public liveChatService: LiveChatService,
    private authManager: AuthManagerService,
    private socialTagsService: SocialTagsService,
    private ccService: NgcCookieConsentService,
    private gtmService: GoogleTagManagerService,
    private _iconRegistry: MatIconRegistry,
    private _sanitizer: DomSanitizer,
    private googleMapsService: GoogleMapsService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) { 
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  private getChild(activatedRoute: ActivatedRoute): ActivatedRoute {
    if (activatedRoute.firstChild) {
      return this.getChild(activatedRoute.firstChild);
    } else {
      return activatedRoute;
    }
  }

  ngOnInit(): void {
    // Managing referrals
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart || event instanceof NavigationEnd),
    )
    .subscribe((event: NavigationStart | NavigationEnd) => {
        if (event instanceof NavigationStart) {
          const parsedUrl = this.router.parseUrl(event.url);
          if (parsedUrl.queryParams.ref && !this.userService.options.referrer) {
            this.userService.options.referrer = parsedUrl.queryParams.ref.toLowerCase();
            this.userService.updateWhiteLabel();
          }
        } else {
          const parsedUrl = this.router.parseUrl(event.url);
          if (typeof(parsedUrl.queryParams.ref) !== 'undefined') {
            delete parsedUrl.queryParams.ref;
            this.location.replaceState(parsedUrl.toString());
          }

          this.sendNavigationGtmData(event);
        }
    });

    if (this.isBrowser) {
      this.googleMapsService.loadGoogleMapsApi().then(() => {
        this.apiLoaded = true;
      }).catch(err => console.error(err));
    }

    this.router.events.pipe(
      filter(event => event instanceof NavigationStart),
    )
      .subscribe((event: NavigationStart) => {
        this.userService.options.lastUrlRequested = event.url;
      });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
    )
    .subscribe(event => {
      // Segmentio page tracking event
      if (this.isBrowser) {
        window.analytics.page();
      }

      // Updating canonical url
      this.canonicalService.updateURL();

      const activatedRoute = this.getChild(this.activatedRoute);
      activatedRoute.data.subscribe(data => {
        // Update user cached information if nothing is stored and the user is authenticated (so the header can display the information)
        // This is usually triggered when the user has the auth token stored and load the website or hit the refresh button in a page
        // that doesn't require authentication. At this point, we need to call the getMe() so we can update the user information.
        if (this.authManager.isAuthenticated && !this.userService.user) {
          this.userService.options.disableUnauthRedirect = true;
          this.userService.getMe()
            .subscribe(
              () => {
                this.userService.options.disableUnauthRedirect = false;
              },
              err => {
                if (err instanceof HttpErrorResponse && err.status !== 401) {
                  this.userService.options.disableUnauthRedirect = false;
                }
              }
            );
        }

        // Updating title and description of the page based on router data information
        this.socialTagsService.setTitle(data.meta?.title);
        this.socialTagsService.setDescription(data.meta?.description);
        this.socialTagsService.setImage(data.meta?.image);

        // Update prerender status code meta tag
        this.metaService.updateTag({
          name: 'prerender-status-code',
          content: data.meta?.prerenderStatusCode || 200
        });
      });
    });

    this.liveChatService.showChatWidget();

    this.popupInitialized$ = this.ccService.initialized$.subscribe(() => {
      if (this.ccService.hasConsented()){
        this.liveChatService.enableCookies(true);
      }
    })


    this.statusChangeSubscription = this.ccService.statusChange$.subscribe(
      (event: NgcStatusChangeEvent) => {
        if (event.status === 'allow' && this.isBrowser && window.analytics.load) {
          this.liveChatService.enableCookies(true);
          // window.analytics.load(environment.segmentKey);
        } else if (event.status === 'deny') {
          this.liveChatService.enableCookies(false);

          if (this.isBrowser) {
            const cookies = document.cookie.split(';');
            cookies.forEach(cookie => {
              const cookieName = cookie.trim().split('=')[0].trim();
              if (cookieName !== 'cookieconsent_status') {
                document.cookie = `${cookieName}=;domain=${environment.domain};max-age=0`;
                document.cookie = `${cookieName}=;domain=www.${environment.domain};max-age=0`;
              }
            });
          }

          if (this.isBrowser) {
            for (let [key, value] of Object.entries(localStorage)) {
              if (key !== ACCESS_TOKEN_KEY) {
                localStorage.removeItem(key);
              }
            }
          }
          if (this.isBrowser && !window.analytics.load) {
            location.reload();
          }
        }
      });

    this.initSvgIcons();
  }

  private sendNavigationGtmData(event: NavigationEnd) {
    this.gtmService.pushTag({
      event: 'NavigationEnd',
      pageUrl: event.url,
      navigationEvent: event,
      environment: environment.name
    });
  }

  ngOnDestroy() {
    this.statusChangeSubscription.unsubscribe();
  }

  private initSvgIcons() {
    this._iconRegistry.addSvgIcon(
      'free-registration-icon',
      this._sanitizer.bypassSecurityTrustResourceUrl(
          'assets/images/identibase/identi-table-icons/free-registration.svg'
      )
  );
  }

}
